<script>
import VuePersianDatetimePicker from 'vue-persian-datetime-picker';
import jMoment from 'moment-jalaali';

export default {
  name: 'DatePickerInput',
  components: {
    VuePersianDatetimePicker,
  },
  props: {
    value: {
      type: [String, Array, Object],
      default: '',
    },
    initialValue: {
      type: [String, Array, Object],
      default: () => jMoment(),
    },
    ranges: {
      type: Array,
      default: () => [],
    },
    format: {
      type: String,
      default: 'x',
    },
    locale: {
      type: String,
      default: 'fa',
      validator(value) {
        return ['fa', 'en'].includes(value);
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedDate: this.parseValue(),
      selectedRange: null,
    };
  },

  computed: {
    isRange() {
      return Boolean(this.ranges?.length);
    },
    formattedSelectedDate() {
      if (!this.selectedDate) return '';

      if (this.isRange) {
        return this.selectedDate.map?.(d => d.format(this.format));
      }

      return this.selectedDate.format?.(this.format);
    },
    formattedInitialValue() {
      if (jMoment.isMoment(this.initialValue)) {
        return this.initialValue?.format(this.format);
      }
      return this.initialValue;
    },
    displayDate() {
      if (!this.selectedDate) return '';

      if (this.isRange) {
        return `${this.selectedDate[0]
          ?.locale(this.locale)
          .format?.(this.displayFormat)} ~ ${this.selectedDate[1]
          ?.locale(this.locale)
          ?.format?.(this.displayFormat)}`;
      }

      return this.selectedDate?.locale(this.locale)?.format(this.displayFormat);
    },
    displayFormat() {
      if (this.isRange) return this.locale === 'en' ? 'MMM D' : 'jD jMMMM';
      return this.locale === 'en' ? 'D MMMM YYYY' : 'jD jMMMM jYYYY';
    },
  },

  watch: {
    formattedSelectedDate: {
      immediate: true,
      handler(value, prevValue) {
        if (String(value) === String(prevValue)) return;
        this.$emit('input', value);
      },
    },
    selectedDate: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.syncSelectedRange();
        });
      },
    },
    value: {
      immediate: true,
      handler() {
        this.selectedDate = this.parseValue();
      },
    },
  },

  methods: {
    parseValue() {
      if (!this.value) return null;
      // todo: remove this after upgrading moment to support 'x' format
      const normalizedFormat = this.format === 'x' ? 'X' : this.format;
      const normalizedValue =
        this.format === 'x' ? this.value?.slice(0, -3) : this.value;

      if (this.ranges?.length) {
        return normalizedValue.map(v => jMoment(v, normalizedFormat));
      }

      return jMoment(normalizedValue, normalizedFormat);
    },
    updateSelections(date) {
      this.selectedDate = date;
    },
    syncSelectedRange() {
      const matchedRange = this.ranges.find(range =>
        this.rangeMatch(range.value, this.selectedDate),
      );
      this.selectedRange = matchedRange?.value;
    },
    rangeMatch(range1, range2) {
      if (!range1 || !range2) return false;
      return (
        range1[0]?.startOf('day').isSame(range2[0]?.startOf('day')) &&
        range1[1]?.startOf('day').isSame(range2[1]?.startOf('day'))
      );
    },
  },
};
</script>

<template>
  <div
    class="date-picker-input"
    :class="{ 'date-picker-input--range': isRange }"
  >
    <ElInput
      :id="`date-picker-input-${_uid}`"
      ref="pickerInput"
      :value="displayDate"
      class="date-picker-input__picker-input"
      prefix-icon="el-icon-date"
      v-bind="$attrs"
    />

    <div>
      <ElSelect
        v-if="isRange"
        v-model="selectedRange"
        class="date-picker-input__range-select"
        @change="selectedDate = $event"
      >
        <ElOption
          v-for="item in ranges"
          :key="item.label"
          :label="item.label"
          :value="item.value"
        />
      </ElSelect>
    </div>

    <VuePersianDatetimePicker
      :value="formattedSelectedDate"
      :initial-value="formattedInitialValue"
      :range="isRange"
      clearable
      auto-submit
      convert-numbers
      :format="format"
      :display-format="displayFormat"
      :custom-input="`#date-picker-input-${_uid}`"
      class="date-picker-input__calendar"
      color="#0ea960"
      :locale="locale"
      v-bind="$attrs"
      @change="updateSelections"
    />
  </div>
</template>

<style lang="scss">
.date-picker-input {
  display: flex;
  align-items: center;

  &__picker-input input {
    border-radius: 8px;
  }

  &--range &__picker-input input {
    border-radius: 8px 0 0 8px;

    @include rtl() {
      border-radius: 0 8px 8px 0;
    }
  }

  &__range-select input {
    border-radius: 8px 0 0 8px;
    background-color: #f7f7f7;
    border-left-color: transparent;

    @include rtl() {
      border-left-color: $--border-color-base;
      border-right-color: transparent;
    }
  }

  &--range &__range-select input {
    border-radius: 0 8px 8px 0;

    @include rtl() {
      border-radius: 8px 0 0 8px;
    }
  }

  // customize date picker styles
  .vpd-header,
  .vpd-actions {
    text-align: center;
  }

  .vpd-content {
    border-radius: 12px;
    overflow: hidden;
  }

  .vpd-month-label {
    width: auto;
  }
}
</style>
